import {
	Box,
	CircularProgress,
	Divider,
	FormControlLabel,
	IconButton,
	MenuItem,
	Select,
	SelectChangeEvent,
	Switch,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { styled } from 'styled-components';
import { CrossIcon, DeleteIcon, DuplicateIcon, EditIcon, LockIcon, TickIcon } from '../Icons';
import SearchBar from '../SearchBar';
import CustomButton from '../CustomButton/CustomButton';
import CustomDataGrid2 from '../CustomDataGrid/CustomDataGrid2';
import CustomSwitch from '../CustomSwitch/CustomSwitch';
import CustomDatePicker from '../CustomDatePicker/CustomDatePicker';
import LabeledSelectField from '../CustomSelect/LabeledSelectField';
import { Catalog, CustomAlertType, Product, SelectedItems, priceRegex } from '@src/pages/Catalogs';
import {
	convertUTCDateIntoIndianTimezone,
	formatPriceValue,
	getUniqueObjectsByKey,
	truncateField,
} from '@src/helpers/Helpers';
import DataNotFound from '../DataItemFound/DataNotFound';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import { AxiosError, AxiosResponse } from 'axios';
import LabeledTextField from '../CustomTextField/LabeledTextField';
import CustomAlert from '../CustomAlert/CustomAlert';
import moment from 'moment';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppSelector } from '@src/store/hooks';
import ProductDetails from '../ProductDetails/ProductDetails';
import CustomModal from '../CustomModal/CustomModal';
import { IsMobileWidth } from '../MediaQueryUtils';
import { RESTAURANT } from '@src/Constants';
import usePermission from '@src/hooks/usePermission';
import { ALL_PERMISSION_MAP } from '@src/helpers/Permission';
import CustomPermission from '../Permissions/CustomPermission';
import PaginatedProductData from '@src/pages/Catalogs/Components/PaginatedProductData';
import CustomTooltip from '../CustomTootip/CustomTooltip';
import { Filters } from '@src/interfaces/type';
import Loading from '../Loading/Loading';

const Container = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: auto;
	padding: 20px;
	gap: 20px;
	overflow: scroll;
	@media screen and (max-width: 600px) {
		gap: 0;
		padding: 0 15px;
		margin-bottom: 68px;
	}
`;

const AddContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: auto;
	gap: 15px;
`;

const GridContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 20px;
`;

const GridRow = styled.div`
	display: flex;
	gap: 20px;
`;

const GridItem = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	font-size: 16px;
	font-weight: 600;
	color: var(--color-font-grey);
`;

const StyledDataGridContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 15px;
	color: var(--color-font-grey);
`;

const StyledTextFieldCustomerPrice = styled(TextField)`
	input {
		height: 47px;
		width: 151px;
		box-sizing: border-box;
		border: 1.5px solid #acacac;
		background: var(--chat-header-bg);
		text-align: center;
	}
`;
const SelectedItemsContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 5px;
	font-size: 16px;
	font-weight: 750;
	line-height: 21.82px;
	transition: width 0.3s ease;
`;

//For Mobile View
const StyledBottomBtnContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 68px;
	padding: 20px;
	background: var(--chat-header-bg);
	border: 1.5px solid var(--gray-color-light);
	z-index: 100;
	position: fixed;
	bottom: 0;
`;

const CatalogForm = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	width: 100%;
	padding: 20px;
	gap: 15px;
	@media screen and (max-width: 600px) {
		justify-content: center;
	}
`;

const productDetailsModalStyles = {
	backgroundColor: 'background.paper',
	borderRadius: '4px',
	boxShadow: '24px',
	maxWidth: '600px',
	maxHeight: '444px',
	'@media (max-width: 600px)': {
		width: '90%',
		maxWidth: '90%',
		maxHeight: '645px',
		height: '645px',
	},
};

type SetProducts = React.Dispatch<React.SetStateAction<Product[]>>;

type CatalogsListingProps = {
	customers: any;
	setCustomers: any;
	catalogs: Catalog[];
	setCatalogs: (daat: Catalog[]) => void;
	filteredCatalogs: Catalog[];
	setFilteredCatalogs: (data: Catalog[]) => void;
	products: Product[];
	setProducts: any;
	loadingData: boolean;
	displayCustomError: (data: AxiosError) => void;
	alert: CustomAlertType;
	setAlert: (data: CustomAlertType) => void;
	setTabValue: (data: string) => void;
	setApiCall: (data: boolean) => void;
	totalPages: number;
	setCategoryId: (data: string) => void;
	page: number;
	setPage: (data: number) => void;
	searchValue: string;
	setSearchValue: (data: string) => void;
	searching: boolean;
	handleChange: any;
	setSearching: (data: boolean) => void;
	selectedItemIds: string[];
	setSelectedItemIds: (updateFunction: (prevState: string[]) => string[]) => void;
	selectedProducts: any;
	setSelectedProducts: any;
	productCount: number;
	filters: Filters | null;
	setFilters: (data: Filters) => void;
	loadingProducts: boolean;
	setLoadingProducts: (data: boolean) => void;
};

export const CatalogsAdd: React.FC<CatalogsListingProps> = ({
	customers,
	products,
	setProducts,
	loadingData,
	displayCustomError,
	alert,
	setAlert,
	setTabValue,
	setApiCall,
	totalPages,
	setCategoryId,
	page,
	setPage,
	searchValue,
	setSearchValue,
	handleChange,
	searching,
	setSearching,
	selectedItemIds,
	setSelectedItemIds,
	selectedProducts,
	setSelectedProducts,
	productCount,
	filters,
	setFilters,
	loadingProducts,
	setLoadingProducts,
}) => {
	const { apiService }: any = React.useContext(ApplicationContext);
	const currentUser: any = useAppSelector((state) => state?.currentUser?.value);
	const navigate = useNavigate();

	const [draft, setDraft] = useState(false);
	const [loading, setLoading] = useState(false);
	const [disable, setDisable] = useState(false);
	const [productDetailsModal, setProductDetailsModal] = useState(false);
	const [productDetailsData, setProductDetailsData] = useState([]);

	//Catalog Form States
	const [catalogName, setCatalogName] = useState<string>('');
	const [customerId, setCustomerId] = useState<string>('');
	const [effectToDate, setEffectToDate] = useState<Date | null>(null);
	const [effectFromDate, setEffectFromDate] = useState<Date | null>(null);
	const [selectedItems, setSelectedItems] = useState<Product[]>([]);
	const [productDetails, setProductDetails] = useState<Product | null>(null);
	const [detailsMiodal, setDetailsModal] = useState(false);
	const { isDrawerOpen } = useAppSelector((state) => state.UI);
	const isMobileWidth = IsMobileWidth();
	const { id } = useParams();

	//Permission
	const checkPermission = usePermission();
	const canCreateCatalogs = checkPermission(ALL_PERMISSION_MAP?.SUPPLIER?.CATALOG?.CREATE, id);
	const canViewProduct = checkPermission(ALL_PERMISSION_MAP?.SUPPLIER?.PRODUCT?.VIEW, id);

	//Mobile Screen States
	const [windowDimensions, setWindowDimensions] = useState({
		width: window.innerWidth,
		height: window.innerHeight,
	});

	const updateWindowDimensions = () => {
		setWindowDimensions({
			width: window.innerWidth,
			height: window.innerHeight,
		});
	};

	const catalogAddView = () => {
		if (windowDimensions?.width > 1100) {
			return (
				<GridContainer>
					<div style={{ display: 'flex', justifyContent: 'space-between' }}>
						<GridRow>
							<GridItem>
								<LabeledTextField
									label="Catalog Name"
									value={catalogName}
									onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
										setCatalogName(e?.target?.value as string)
									}
									placeholder="Catalog Name"
								/>
							</GridItem>
							<GridItem>
								<LabeledSelectField
									label="Customer Name"
									placeholder="Name"
									options={customers}
									value={customerId}
									handleChange={handleSelectChange}
									styles={{ width: '344px', height: '36px' }}
								/>
							</GridItem>
						</GridRow>
						<CustomSwitch
							title={draft ? 'Publish' : 'Draft'}
							color="customGreen"
							checked={draft}
							onToggle={handleSwitchToggle}
						/>
					</div>
					<GridRow>
						<GridItem>
							<CustomDatePicker
								label="Effective From"
								styles={{ width: '344px', height: '36px' }}
								selectedDate={effectFromDate}
								onDateChange={handleEffectFromDate}
							/>
						</GridItem>
						<GridItem>
							<CustomDatePicker
								label="Effective To"
								styles={{ width: '344px', height: '36px' }}
								minDate={effectFromDate}
								selectedDate={effectToDate}
								onDateChange={handleEffectToDate}
							/>
						</GridItem>
					</GridRow>
				</GridContainer>
			);
		} else if (windowDimensions?.width > 600) {
			return (
				<CatalogForm>
					<div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
						<CustomSwitch
							title={draft ? 'Publish' : 'Draft'}
							color="customGreen"
							checked={draft}
							onToggle={handleSwitchToggle}
						/>
					</div>
					<LabeledTextField
						label="Catalog Name"
						value={catalogName}
						onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
							setCatalogName(e?.target?.value as string)
						}
						styles={{ width: '70vw', height: '36px' }}
						placeholder="Catalog Name"
					/>
					<LabeledSelectField
						label="Customer Name"
						placeholder="Name"
						options={customers}
						value={customerId}
						handleChange={handleSelectChange}
						styles={{ width: '70vw', height: '36px' }}
					/>
					<CustomDatePicker
						label="Effective From"
						styles={{ width: '70vw', height: '36px' }}
						selectedDate={effectFromDate}
						onDateChange={handleEffectFromDate}
					/>
					<CustomDatePicker
						label="Effective To"
						styles={{ width: '70vw', height: '36px' }}
						minDate={effectFromDate}
						selectedDate={effectToDate}
						onDateChange={handleEffectToDate}
					/>
				</CatalogForm>
			);
		} else {
			return (
				<CatalogForm style={{ padding: '10px' }}>
					<div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
						<CustomSwitch
							title={draft ? 'Publish' : 'Draft'}
							color="customGreen"
							checked={draft}
							onToggle={handleSwitchToggle}
						/>
					</div>
					<LabeledTextField
						label="Catalog Name"
						value={catalogName}
						onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
							setCatalogName(e?.target?.value as string)
						}
						styles={{ width: '100%', height: '36px' }}
						placeholder="Catalog Name"
					/>
					<LabeledSelectField
						label="Customer Name"
						placeholder="Name"
						options={customers}
						value={customerId}
						handleChange={handleSelectChange}
						styles={{ width: '328px', height: '36px' }}
					/>
					<CustomDatePicker
						label="Effective From"
						styles={{ width: '328px', height: '36px' }}
						selectedDate={effectFromDate}
						onDateChange={handleEffectFromDate}
					/>
					<CustomDatePicker
						label="Effective To"
						styles={{ width: '328px', height: '36px' }}
						minDate={effectFromDate}
						selectedDate={effectToDate}
						onDateChange={handleEffectToDate}
					/>
				</CatalogForm>
			);
		}
	};

	const handleEditClick = (params: any) => {
		const updatedCatalogs = products?.map((catalog) => {
			if (params?.id === catalog?.id) {
				return { ...catalog, edit: true, focus: true };
			}
			return catalog;
		});
		setProducts(updatedCatalogs);
	};

	const handleEditClose = (params: any) => {
		const updatedCatalogs = products.map((catalog) => {
			if (params?.id === catalog.id) {
				return { ...catalog, edit: false, customerPrice: catalog?.oldCustomerPrice };
			}
			return catalog;
		});
		setProducts(updatedCatalogs);
	};

	const handleUpdate = (params: any) => {
		const updatedProducts = products.map((catalog) => {
			if (params.id === catalog.id) {
				return {
					...catalog,
					edit: false,
					focus: false,
					customerPrice: formatPriceValue(catalog?.customerPrice),
					oldCustomerPrice: undefined,
				};
			}
			return catalog;
		});
		setProducts(updatedProducts);

		//update selected products
		setSelectedProducts((prevState: any) => {
			return prevState?.map((product: any) => {
				if (product?.id === params.id) {
					return {
						...product,
						customerPrice: formatPriceValue(params?.customerPrice),
						oldCustomerPrice: undefined,
					};
				}
				return product;
			});
		});
	};

	const handleCustomerPrice = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		params: any
	) => {
		const inputValue = e.target.value;

		if (priceRegex.test(inputValue)) {
			const updatedProducts: Product[] = products?.map((catalog) => {
				if (params.id === catalog.id) {
					return {
						...catalog,
						customerPrice: inputValue,
						oldCustomerPrice:
							catalog?.oldCustomerPrice === undefined
								? catalog.customerPrice
								: catalog?.oldCustomerPrice,
					};
				}
				return catalog;
			});

			setProducts(updatedProducts);

			//update selected products
			setSelectedProducts((prevState: any) => {
				return prevState?.map((product: any) => {
					if (product?.id === params.id) {
						return {
							...product,
							customerPrice: inputValue,
							oldCustomerPrice:
								product?.oldCustomerPrice === undefined
									? product.customerPrice
									: product?.oldCustomerPrice,
						};
					}
					return product;
				});
			});
		}
	};

	const columns = [
		// {
		// 	field: 'id',
		// 	headerName: 'Product ID',
		// 	width: 100,
		// 	headerAlign: 'center',
		// 	align: 'center',
		// 	renderCell: (params: any) => {
		// 		const productId = params.row.id;
		// 		return <div title={productId}>{truncateField(productId, 32)}</div>;
		// 	},
		// },
		{
			field: 'name',
			headerName: 'Part Name',
			width: 300,
			renderCell: (params: any) => {
				const name = params?.row?.name;
				return (
					<div style={{ display: 'flex', gap: '8px', alignItems: 'center' }} title={name}>
						<img
							style={{ objectFit: 'contain' }}
							src={
								params?.row?.images?.[0]?.image ||
								params?.row?.images?.[0]?.url ||
								params?.row?.image ||
								'/placeholderImage.svg'
							}
							alt="product-img"
							width="24px"
							height="24px"
						/>
						{params?.row?.name?.length > 33 ? (
							<CustomTooltip title={params?.row?.name} placement="top">
								<p
									style={{
										maxWidth: '260px',
										whiteSpace: 'nowrap',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
									}}
								>
									{params?.row?.name}
								</p>
							</CustomTooltip>
						) : (
							<p
								style={{
									maxWidth: '260px',
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
								}}
							>
								{params?.row?.name}
							</p>
						)}
					</div>
				);
			},
		},
		{ field: 'unit', headerName: 'Unit', width: 80 },
		{ field: 'price', headerName: 'Price (INR)', width: 100, align: 'center' },
		{
			field: 'category',
			headerName: 'Part Category',
			filterable: true,
			width: 160,
		},
		{
			field: 'customerPrice',
			headerName: 'Customer-Price',
			width: 130,
			renderCell: (params: any) =>
				params?.row?.edit ? (
					<StyledTextFieldCustomerPrice
						id="editPrice"
						variant="standard"
						value={params?.row?.customerPrice}
						autoFocus={params?.row?.focus}
						onChange={(e) => {
							handleCustomerPrice(e, params);
						}}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								handleUpdate(params);
							}
						}}
						InputProps={{
							disableUnderline: true,
							classes: {
								root: 'MuiInputBase-root',
							},
						}}
					/>
				) : (
					params.row.customerPrice
				),
			align: 'center',
			headerAlign: 'center',
		},
		{
			field: 'actions',
			headerName: 'Actions',
			width: 120,
			renderCell: (params: any) =>
				params?.row?.edit ? (
					<div
						id="updateBtns"
						style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							gap: '5px',
							width: '100%',
							height: '100%',
						}}
					>
						<IconButton id="updateProductBtn" onClick={() => handleUpdate(params)}>
							<TickIcon id="updateProduct" />
						</IconButton>
						<IconButton id="cancelProductBtn" onClick={() => handleEditClose(params)}>
							<CrossIcon id="cancelProduct" />
						</IconButton>
					</div>
				) : (
					<div
						id="editBtns"
						style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							width: '100%',
							height: '100%',
						}}
					>
						{params?.row?.editBtnDisable ? (
							<CustomTooltip title="Please Select Item First" placement="top">
								<span>
									<IconButton>
										<EditIcon id="editProduct1" />
									</IconButton>
								</span>
							</CustomTooltip>
						) : (
							<IconButton
								disabled={params?.row?.editBtnDisable}
								onClick={() => handleEditClick(params)}
								id="editProductBtn"
							>
								<EditIcon id="editProduct" />
							</IconButton>
						)}
					</div>
				),
			align: 'center',
			headerAlign: 'center',
		},
	];

	const handleRowClick = (params: any, event: any) => {
		const id = event?.target?.id;
		console.log(event?.target);
		if (
			![
				'editProduct',
				'cancelProduct',
				'updateProduct',
				'editProductBtn',
				'cancelProductBtn',
				'updateProductBtn',
				'editPrice',
				'editBtns',
				'updateBtns',
				'editProduct1',
			]?.includes(id) &&
			!params?.row?.edit
		) {
			const data = products?.find((product: any) => product?.id === params?.id);
			const details: any = [
				{
					name: data?.name,
					images: data?.images?.map((image: any) => {
						return { src: image?.image, alt: 'product-img' };
					}),
					description: data?.description,
					details: [
						`Part Category: ${data?.category}`,
						`Unit: ${data?.unit}`,
						`Price: ${data?.price}`,
					],
				},
			];
			setProductDetailsData(details);
			setProductDetailsModal(true);
		}
	};

	const handleSpecificProductSelectMC = (id: any) => {
		const ids: string[] = [];

		products?.forEach((item: any) => {
			if ((item?.id !== id && item?.isSelection) || (item?.id === id && !item?.isSelection)) {
				ids.push(String(item?.id));
			}
		});

		//@ts-ignore
		onRowsSelectionHandler(ids);
	};

	const onRowsSelectionHandler = (ids: string[]) => {
		const unselectedProductIds: string[] = [];
		const selectedProducts: Product[] = products?.filter((product: Product) =>
			ids?.includes(product?.id)
		);
		const updatedProducts: Product[] = products?.map((product: Product) => {
			if (ids?.includes(product?.id)) {
				return { ...product, editBtnDisable: false, selected: true, isSelection: true };
			}
			unselectedProductIds.push(product?.id);
			return { ...product, editBtnDisable: true, selected: false, isSelection: false };
		});
		setProducts(updatedProducts);
		setSelectedItems(selectedProducts);

		//Maintain other pages selected items
		setSelectedItemIds((prevState: string[]) => {
			const filterIds = prevState?.filter((id) => !unselectedProductIds?.includes(id));
			return [...new Set([...filterIds, ...ids])];
		});
		setSelectedProducts((prevState: any) => {
			const uniqueIds = new Set(prevState.map((product: any) => product?.id));
			const filteredData = selectedProducts.filter((product: any) => !uniqueIds.has(product?.id));
			const filteredPrevState = prevState?.filter(
				(product: any) => !unselectedProductIds?.includes(product?.id)
			);

			return [...filteredPrevState, ...filteredData];
		});
	};

	const showDetails = (product: any) => {
		setProductDetails(product);
		setDetailsModal(true);
	};

	const disabledCreateCatalog = () => {
		if (
			!selectedItemIds?.length ||
			!catalogName ||
			!customerId ||
			!effectFromDate ||
			!effectToDate ||
			disable
		) {
			return true;
		}
	};

	const handleEffectToDate = (date: Date | null) => {
		setEffectToDate(convertUTCDateIntoIndianTimezone(date as Date));
	};

	const handleEffectFromDate = (date: Date | null) => {
		setEffectFromDate(convertUTCDateIntoIndianTimezone(date as Date));
	};

	const handleSwitchToggle = (newChecked: boolean) => {
		setDraft(newChecked);
	};

	const handleSelectChange = (event: SelectChangeEvent<unknown>) => {
		const selectedValue = event.target.value as string;
		setCustomerId(selectedValue);
	};

	const clearFields = () => {
		const updatedProducts: Product[] = products?.map((product: Product) => {
			return { ...product, customerPrice: '', selected: false };
		});
		setCatalogName('');
		setCustomerId('');
		setEffectFromDate(null);
		setEffectToDate(null);
		setSelectedItems([]);
		setSelectedItemIds((prevState) => []);
		setProducts(updatedProducts);
	};

	const createProducts = (data: any, id: any) => {
		apiService.addProducts(
			data,
			(response: AxiosResponse) => {
				setAlert({
					message: 'Catalog Created Successfully',
					severity: 'success',
				});
				clearFields();
				setLoading(false);
				setDisable(false);
				navigate(
					currentUser?.type?.toLowerCase() === RESTAURANT
						? `/restaurants/${id}/catalogs`
						: `/suppliers/${id}/catalogs`
				);
			},
			(error: AxiosError) => {
				displayCustomError(error);
				setLoading(false);
				setDisable(false);
			},
			id
		);
	};

	const createCatalog = () => {
		setLoading(true);
		setDisable(true);

		const from = moment(new Date(effectFromDate as Date)).format('YYYY-MM-DD');
		const to = moment(new Date(effectToDate as Date)).format('YYYY-MM-DD');
		const data = {
			restaurantId: customerId,
			from: effectFromDate ? from : '',
			to: effectToDate ? to : '',
			name: catalogName,
			status: draft ? 'Published' : 'Draft',
		};

		const productsData = selectedProducts?.map((item: any) => {
			return {
				id: item?.id,
				price: item?.customerPrice || item?.price,
			};
		});

		apiService.createCatalog(
			data,
			(response: AxiosResponse) => {
				const catalogResponse = response.data.data;
				setApiCall(true);
				//next call
				createProducts(
					{ products: productsData },
					catalogResponse?.catalogId || catalogResponse?.catalog_id
				);
			},
			(error: AxiosError) => {
				setLoading(false);
				setDisable(false);
				displayCustomError(error);
			}
		);
	};

	//For Mobile Screen
	useEffect(() => {
		window.addEventListener('resize', updateWindowDimensions);

		return () => {
			window.removeEventListener('resize', updateWindowDimensions);
		};
	}, []);

	useEffect(() => {
		updateWindowDimensions();
	}, []);

	//Reset state on unmount
	useEffect(() => {
		return () => {
			setProducts((prevState: Product[]) => {
				return prevState?.map((product: Product) => {
					return { ...product, selected: false };
				});
			});
			setSelectedItemIds((prevState) => []);
			setSelectedItems([]);
			setSelectedProducts([]);
		};
	}, []);

	if (loadingData) {
		return <Loading />;
	}
	const modals = () => {
		if (detailsMiodal && productDetails) {
			return (
				<CustomModal
					open={detailsMiodal}
					setOpen={setDetailsModal}
					header={false}
					style={productDetailsModalStyles}
				>
					{/* @ts-ignore */}
					<ProductDetails data={productDetails} setOpenModal={setDetailsModal} />
				</CustomModal>
			);
		}
	};

	return (
		<>
			{canCreateCatalogs ? (
				<>
					{modals()}
					<Container>
						<AddContainer>
							{windowDimensions?.width > 600 ? (
								<div style={{ display: 'flex', justifyContent: 'flex-end' }}>
									<CustomButton
										title={
											loading ? (
												<CircularProgress
													size={20}
													style={{ color: 'var(--default-text-color)' }}
												/>
											) : (
												'Create Catalog'
											)
										}
										handleClick={createCatalog}
										heightVariant="medium"
										color="primary"
										styles={{
											width: '134px',
											height: '36px',
											padding: '7px 12px',
										}}
										disabled={disabledCreateCatalog()}
									/>
								</div>
							) : null}
							{catalogAddView()}
						</AddContainer>
						<Divider />
						<PaginatedProductData
							categoryOptions={getUniqueObjectsByKey(products, 'categoryId')}
							rows={products}
							columns={columns}
							onRowsSelectionHandler={onRowsSelectionHandler}
							handleRowClick={handleRowClick}
							page={page}
							setPage={setPage}
							totalPages={totalPages}
							setCategoryId={setCategoryId}
							handleChange={handleChange}
							selectedItemIds={selectedItemIds}
							searchValue={searchValue}
							setSearchValue={setSearchValue}
							searching={searching}
							setSearching={setSearching}
							controlRowSelection
							productCount={productCount}
							style={{
								transition: 'height 0.3s ease',
								maxHeight: 'calc(100vh - 370px)',
								minHeight: 'calc(100vh - 370px)',
							}}
							filters={filters}
							setFilters={setFilters}
							handleSpecificProductSelect={handleSpecificProductSelectMC}
							showDetails={showDetails}
							productContainerHeight={'calc(100vh - 280px)'}
							loadingProducts={loadingProducts}
							setLoadingProducts={setLoadingProducts}
							handleEditClick={handleEditClick}
							handleUpdate={handleUpdate}
							handleEditClose={handleEditClose}
							StyledTextFieldCustomerPrice={StyledTextFieldCustomerPrice}
							handleCustomerPrice={handleCustomerPrice}
						/>

						{/* <StyledDataGridContainer>
							{windowDimensions?.width > 600 ? (
								<Typography variant="body1">
									Select products and click the button to successfully create a new catalog.
								</Typography>
							) : null}
							{canViewProduct ? (
								products?.length > 0 ? (
									<CustomDataGrid2
										filterOptions={getUniqueObjectsByKey(products, 'categoryId')}
										rows={products}
										columns={columns}
										onRowsSelectionHandler={onRowsSelectionHandler}
										handleRowClick={handleRowClick}
									/>
								) : (
									<DataNotFound content="No Products Found" height="30vh" />
								)
							) : (
								<CustomPermission
									icon={<LockIcon />}
									title="We are sorry..."
									paragraph="You do not have permission to view products. Contact 
						an administrator to get permission."
									containerStyle={{ height: 'calc(100vh - 340px)' }}
								/>
							)}
						</StyledDataGridContainer> */}
					</Container>
					{windowDimensions?.width <= 600 ? (
						<StyledBottomBtnContainer>
							<CustomButton
								title={
									loading ? (
										<CircularProgress size={20} style={{ color: 'var(--default-text-color)' }} />
									) : (
										'Create Catalog'
									)
								}
								handleClick={createCatalog}
								color="primary"
								styles={{
									width: '328px',
									height: '36px',
								}}
								disabled={disabledCreateCatalog()}
							/>
						</StyledBottomBtnContainer>
					) : null}
				</>
			) : (
				<CustomPermission
					icon={<LockIcon />}
					title="We are sorry..."
					paragraph="You do not have permission to create catalog. Contact 
				an administrator to get permission."
				/>
			)}
		</>
	);
};
